/*
 * The open app tabs, the active one and the order they were last used,
 * persisted per user in localStorage. The URL is the source of truth for the
 * active tab: the bar's route sync calls `visit` on every location change.
 * The transitions are pure functions so the rules are unit-tested directly.
 */
import { create } from "zustand";

import { casePath, CASE_HOME_SEGMENT } from "@/features/caseIde/routes";

import { groupKeyOf, groupTabs } from "../groups";
import {
  caseTabId,
  clientIdForRoute,
  homeTarget,
  isKeptPageTab
} from "../tabRoutes";
import type { AppTab, TabTarget } from "../types";

export type TabsSnapshot = {
  tabs: AppTab[];
  activeId: string | null;
  /** Tab ids, most recently used first. */
  recency: string[];
};

export type TabMeta = Partial<
  Pick<
    AppTab,
    "title" | "workflow" | "initials" | "avatarUrl" | "clientId" | "subtitle"
  >
>;

export const EMPTY_TABS: TabsSnapshot = {
  tabs: [],
  activeId: null,
  recency: []
};

const STORAGE_PREFIX = "APP_TABS_v1";
const LEGACY_RECENT_CASES_PREFIX = "RECENTLY_VIEWED_CASES";

export const tabsStorageKey = (uid: string): string =>
  `${STORAGE_PREFIX}_${uid}`;

const bump = (recency: string[], id: string): string[] => [
  id,
  ...recency.filter((x) => x !== id)
];

const fromTarget = (
  target: TabTarget,
  preview: boolean,
  visitedAt?: number
): AppTab => ({
  id: target.id,
  kind: target.kind,
  route: target.route,
  title: target.title,
  pageId: target.pageId,
  ...(target.clientId ? { clientId: target.clientId } : {}),
  preview,
  ...(visitedAt ? { visitedAt } : {})
});

/** Where a new tab goes: after its client's last tab, else after the active tab. */
const insertAt = (state: TabsSnapshot, clientId?: string): number => {
  if (clientId) {
    for (let i = state.tabs.length - 1; i >= 0; i -= 1) {
      if (state.tabs[i].clientId === clientId) return i + 1;
    }
  }
  const activeIdx = state.tabs.findIndex((t) => t.id === state.activeId);
  return activeIdx >= 0 ? activeIdx + 1 : state.tabs.length;
};

/**
 * Go to a location's tab. An open tab is focused (and remembers the new
 * route). Otherwise it replaces the preview tab in place, or opens as a
 * preview after its client's other tabs (else after the active tab). Home and
 * Get Started are the exception: they open as kept tabs. A navigation page
 * (Cases, Clients, ...) still in preview lives only while it is active:
 * leaving it for any other tab closes it, unless it was kept.
 */
export function visitTab(
  state: TabsSnapshot,
  target: TabTarget,
  now: number = Date.now()
): TabsSnapshot {
  const active = state.tabs.find((t) => t.id === state.activeId);
  const leaving =
    active &&
    active.id !== target.id &&
    active.kind === "page" &&
    active.preview
      ? active.id
      : null;

  const existing = state.tabs.find((t) => t.id === target.id);
  if (existing) {
    return {
      tabs: state.tabs
        .filter((t) => t.id !== leaving)
        .map((t) =>
          t.id === target.id
            ? {
                ...t,
                route: target.route,
                clientId: target.clientId ?? t.clientId,
                visitedAt: now
              }
            : t
        ),
      activeId: target.id,
      recency: bump(
        state.recency.filter((x) => x !== leaving),
        target.id
      )
    };
  }

  const preview = target.kind !== "page" || !isKeptPageTab(target.id);
  const tab = fromTarget(target, preview, now);
  const previewIdx = leaving
    ? state.tabs.findIndex((t) => t.id === leaving)
    : preview
    ? state.tabs.findIndex((t) => t.preview && t.id !== target.id)
    : -1;
  if (previewIdx >= 0) {
    const replaced = state.tabs[previewIdx].id;
    const tabs = state.tabs.slice();
    tabs[previewIdx] = tab;
    return {
      tabs,
      activeId: tab.id,
      recency: bump(
        state.recency.filter((x) => x !== replaced),
        tab.id
      )
    };
  }

  const at = insertAt(state, tab.clientId);
  const tabs = [...state.tabs.slice(0, at), tab, ...state.tabs.slice(at)];
  return { tabs, activeId: tab.id, recency: bump(state.recency, tab.id) };
}

/**
 * Close a tab. Closing the active tab activates its right neighbour (else the
 * left); closing the last tab opens Home. `next` is the tab to navigate to
 * when the active tab closed, else null.
 */
export function closeTab(
  state: TabsSnapshot,
  id: string
): { state: TabsSnapshot; next: AppTab | null } {
  const idx = state.tabs.findIndex((t) => t.id === id);
  if (idx < 0) return { state, next: null };
  const rest = state.tabs.filter((t) => t.id !== id);
  const recency = state.recency.filter((x) => x !== id);
  if (!rest.length) {
    const home = fromTarget(homeTarget(), false);
    return {
      state: { tabs: [home], activeId: home.id, recency: [home.id] },
      next: home
    };
  }
  if (id !== state.activeId) {
    return { state: { ...state, tabs: rest, recency }, next: null };
  }
  const next = rest[idx] ?? rest[idx - 1];
  return {
    state: { tabs: rest, activeId: next.id, recency: bump(recency, next.id) },
    next
  };
}

/** Make a preview tab stay (edit, chat, double-click). */
export function keepTab(state: TabsSnapshot, id: string): TabsSnapshot {
  if (!state.tabs.some((t) => t.id === id && t.preview)) return state;
  return {
    ...state,
    tabs: state.tabs.map((t) => (t.id === id ? { ...t, preview: false } : t))
  };
}

const moveItem = <T>(items: T[], from: number, to: number): T[] => {
  const next = items.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

/**
 * Move a whole group (a client's tabs, or a page) to `toIndex` among the
 * groups. Each group's tabs keep their order and end up side by side.
 */
export function moveGroup(
  state: TabsSnapshot,
  key: string,
  toIndex: number
): TabsSnapshot {
  const groups = groupTabs(state.tabs);
  const from = groups.findIndex((g) => g.key === key);
  const to = Math.max(0, Math.min(groups.length - 1, Math.floor(toIndex)));
  if (from < 0 || !Number.isFinite(toIndex) || from === to) return state;
  return {
    ...state,
    tabs: moveItem(groups, from, to).flatMap((g) => g.tabs)
  };
}

/** Move a tab to `toIndex` among its own group's tabs; other groups stay put. */
export function moveTab(
  state: TabsSnapshot,
  id: string,
  toIndex: number
): TabsSnapshot {
  const tab = state.tabs.find((t) => t.id === id);
  if (!tab || !Number.isFinite(toIndex)) return state;
  const key = groupKeyOf(tab);
  const siblings = state.tabs.filter((t) => groupKeyOf(t) === key);
  const from = siblings.indexOf(tab);
  const to = Math.max(0, Math.min(siblings.length - 1, Math.floor(toIndex)));
  if (from === to) return state;
  const reordered = moveItem(siblings, from, to);
  let i = 0;
  return {
    ...state,
    tabs: state.tabs.map((t) => {
      if (groupKeyOf(t) !== key) return t;
      const next = reordered[i] ?? t;
      i += 1;
      return next;
    })
  };
}

export function setTabMeta(
  state: TabsSnapshot,
  id: string,
  meta: TabMeta
): TabsSnapshot {
  const tab = state.tabs.find((t) => t.id === id);
  if (!tab) return state;
  const changed = (Object.keys(meta) as Array<keyof TabMeta>).some(
    (k) => meta[k] !== undefined && meta[k] !== tab[k]
  );
  if (!changed) return state;
  const clean = Object.fromEntries(
    Object.entries(meta).filter(([, v]) => v !== undefined)
  ) as TabMeta;
  return {
    ...state,
    tabs: state.tabs.map((t) => (t.id === id ? { ...t, ...clean } : t))
  };
}

const isTab = (v: unknown): v is AppTab => {
  if (!v || typeof v !== "object") return false;
  const t = v as Record<string, unknown>;
  return (
    typeof t.id === "string" &&
    (t.kind === "case" || t.kind === "client" || t.kind === "page") &&
    typeof t.route === "string" &&
    typeof t.title === "string"
  );
};

/**
 * The saved tabs, or the old recently-viewed cases as kept case tabs on first
 * run. Navigation page tabs are not restored (saved before they became
 * previews, or a preview left open); the route sync reopens the current one.
 */
export function loadTabs(
  uid: string,
  storage: Storage = localStorage
): TabsSnapshot {
  try {
    const raw = storage.getItem(tabsStorageKey(uid));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<TabsSnapshot>;
      const tabs = (Array.isArray(parsed.tabs) ? parsed.tabs : [])
        .filter(isTab)
        .filter((t) => t.kind !== "page" || isKeptPageTab(t.id))
        .map((t) => {
          const clientId =
            (typeof t.clientId === "string" && t.clientId) ||
            clientIdForRoute(t.route);
          return {
            ...t,
            preview: Boolean(t.preview),
            ...(clientId ? { clientId } : {})
          };
        });
      const ids = new Set(tabs.map((t) => t.id));
      const activeId =
        typeof parsed.activeId === "string" && ids.has(parsed.activeId)
          ? parsed.activeId
          : null;
      const recency = (
        Array.isArray(parsed.recency) ? parsed.recency : []
      ).filter((x): x is string => typeof x === "string" && ids.has(x));
      return { tabs, activeId, recency };
    }
    const legacy = storage.getItem(`${LEGACY_RECENT_CASES_PREFIX}_${uid}`);
    if (!legacy) return EMPTY_TABS;
    const cases = JSON.parse(legacy) as unknown;
    if (!Array.isArray(cases)) return EMPTY_TABS;
    const tabs: AppTab[] = cases
      .filter(
        (c): c is { id: string; name?: string; visa?: string } =>
          Boolean(c) && typeof (c as { id?: unknown }).id === "string"
      )
      .map((c) => ({
        id: caseTabId(c.id),
        kind: "case",
        route: casePath({ individualId: c.id }, CASE_HOME_SEGMENT),
        title: c.name || "Case",
        workflow: c.visa || undefined,
        preview: false
      }));
    return { tabs, activeId: null, recency: tabs.map((t) => t.id) };
  } catch {
    return EMPTY_TABS;
  }
}

export function saveTabs(
  uid: string,
  snapshot: TabsSnapshot,
  storage: Storage = localStorage
): void {
  try {
    storage.setItem(tabsStorageKey(uid), JSON.stringify(snapshot));
  } catch {
    // Storage full or blocked: tabs still work for this session.
  }
}

type AppTabsState = TabsSnapshot & {
  uid: string | null;
  hydrate: (uid: string) => void;
  visit: (target: TabTarget) => void;
  close: (id: string) => AppTab | null;
  keep: (id: string) => void;
  moveGroup: (key: string, toIndex: number) => void;
  moveTab: (id: string, toIndex: number) => void;
  setMeta: (id: string, meta: TabMeta) => void;
  reset: () => void;
};

const snapshotOf = (s: TabsSnapshot): TabsSnapshot => ({
  tabs: s.tabs,
  activeId: s.activeId,
  recency: s.recency
});

export const useAppTabsStore = create<AppTabsState>((set, get) => ({
  ...EMPTY_TABS,
  uid: null,
  hydrate: (uid) => {
    if (get().uid === uid) return;
    set({ uid, ...loadTabs(uid) });
  },
  visit: (target) => set(visitTab(snapshotOf(get()), target)),
  close: (id) => {
    const { state, next } = closeTab(snapshotOf(get()), id);
    set(state);
    return next;
  },
  keep: (id) => set(keepTab(snapshotOf(get()), id)),
  moveGroup: (key, toIndex) => {
    const next = moveGroup(snapshotOf(get()), key, toIndex);
    if (next.tabs !== get().tabs) set(next);
  },
  moveTab: (id, toIndex) => {
    const next = moveTab(snapshotOf(get()), id, toIndex);
    if (next.tabs !== get().tabs) set(next);
  },
  setMeta: (id, meta) => {
    const next = setTabMeta(snapshotOf(get()), id, meta);
    if (next.tabs !== get().tabs) set(next);
  },
  reset: () => set({ ...EMPTY_TABS, uid: null })
}));

useAppTabsStore.subscribe((s, prev) => {
  if (!s.uid || s.uid !== prev.uid) return;
  if (
    s.tabs === prev.tabs &&
    s.activeId === prev.activeId &&
    s.recency === prev.recency
  )
    return;
  saveTabs(s.uid, snapshotOf(s));
});

/** Keep the active tab's preview off once the user works in it. */
export const keepActiveTab = (): void => {
  const { activeId, keep } = useAppTabsStore.getState();
  if (activeId) keep(activeId);
};
